/**
 * « Mes histoires » — /settings/stories
 *
 * La liste des histoires sur mesure vit sur son propre écran : avec des
 * dizaines d'histoires, l'inliner dans Réglages noyait le reste de la page.
 * Réglages n'en garde qu'une entrée avec le compteur.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Trash2 } from "lucide-react";
import {
  BackButton, Button, Card, Container, ErrorBanner, Headline, MutedText, PageLayout,
} from "../components/ui";
import { Cover } from "../components/Cover";
import { deleteStory, getMyStories } from "../lib/dataSource";
import type { StoryCard } from "../lib/types";

/**
 * Ligne d'une histoire sur mesure. La suppression vit ici (Réglages) et
 * pas sur la page de l'histoire : l'enfant peut tenir le téléphone, et un
 * geste destructif ne doit pas être à portée de la page qu'il ouvre. Même
 * confirmation en ligne que `ChildCard`. La vignette aide à reconnaître une
 * histoire dans une longue liste.
 */
function StoryRow({ story, onRemove }: { story: StoryCard; onRemove: () => Promise<void> }) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [failed, setFailed] = useState(false);

  return (
    <Card className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <div className="w-12 shrink-0">
          <Cover
            svg={story.cover_svg}
            imageUrl={story.cover_image_url}
            imageThumbUrl={story.cover_image_thumb_url}
            imageSize="thumb"
            title={story.title}
            colorway={story.cover_colorway}
            colorKey={story.slug}
            showTitle={false}
          />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-heading font-semibold text-sm truncate">{story.title}</p>
          {story.child && <MutedText>{story.child.first_name}</MutedText>}
        </div>
        <button
          type="button"
          className="tap-target shrink-0 text-coral"
          onClick={() => setConfirming(true)}
          aria-label={t("myStories.remove")}
        >
          <Trash2 size={18} />
        </button>
      </div>
      {confirming && (
        <div className="flex flex-col gap-3 border-t border-line pt-3" role="alertdialog" aria-label={t("myStories.remove")}>
          <p className="text-sm text-paper">{t("myStories.removeConfirm", { title: story.title })}</p>
          {failed && <ErrorBanner>{t("myStories.removeError")}</ErrorBanner>}
          <div className="flex gap-2">
            <Button variant="secondary" className="flex-1" onClick={() => setConfirming(false)} disabled={removing}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="coral"
              className="flex-1"
              disabled={removing}
              onClick={async () => {
                setRemoving(true);
                setFailed(false);
                try {
                  await onRemove();
                } catch {
                  // La ligne reste : rien n'a été supprimé côté serveur.
                  setFailed(true);
                  setRemoving(false);
                }
              }}
            >
              {t("common.delete")}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

export default function MyStories() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // `null` = chargement ; l'échec a son propre état pour ne pas afficher
  // « aucune histoire » à tort.
  const [stories, setStories] = useState<StoryCard[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getMyStories()
      .then((list) => {
        if (!cancelled) setStories(list);
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <PageLayout>
      <Container className="pt-6 flex flex-col gap-5">
        <BackButton onClick={() => navigate("/settings")} />
        <Headline>{t("myStories.title")}</Headline>

        {loadFailed ? (
          <ErrorBanner>{t("myStories.loadError")}</ErrorBanner>
        ) : stories === null ? (
          <MutedText>{t("common.loading")}</MutedText>
        ) : stories.length === 0 ? (
          <MutedText>{t("myStories.empty")}</MutedText>
        ) : (
          <div className="flex flex-col gap-2">
            {stories.map((s) => (
              <StoryRow
                key={s.id}
                story={s}
                onRemove={async () => {
                  await deleteStory(s.id);
                  setStories((list) => (list ? list.filter((x) => x.id !== s.id) : list));
                }}
              />
            ))}
          </div>
        )}
      </Container>
    </PageLayout>
  );
}
